import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { getAdminAuditLog, type AdminAuditLogEntry } from '../../api/admin.api';
import './admin.css';

function formatActionLabel(value: string | null): string {
  if (!value) return 'Прочее действие';
  return value
    .replace(/^admin\./, '')
    .replace(/\./g, ' / ')
    .replace(/_/g, ' ');
}

function formatEntityLabel(entry: AdminAuditLogEntry): string {
  const base = entry.entityType ? `Сущность: ${entry.entityType}` : 'Сущность: —';
  const id = entry.entityId ? `, ID ${entry.entityId}` : '';
  const label = entry.entityLabel ? `, ${entry.entityLabel}` : '';
  return `${base}${id}${label}`;
}

function parseChangeSummary(summary: string | null): string[] {
  if (!summary) return [];
  return summary
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function AdminAuditLog() {
  const [filters, setFilters] = useState({
    actionType: '',
    entityType: '',
    from: '',
    to: '',
    q: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<AdminAuditLogEntry[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const initializedRef = useRef(false);

  const actionOptions = useMemo(
    () => [
      '',
      'admin.catalog.category.create',
      'admin.catalog.category.update',
      'admin.catalog.category.delete',
      'admin.catalog.category.reorder',
      'admin.catalog.product.create',
      'admin.catalog.product.update',
      'admin.catalog.product.delete',
      'admin.catalog.product.reorder',
      'admin.promocodes.create',
      'admin.promocodes.update',
      'admin.promocodes.delete',
      'admin.export.csv',
      'admin.export.period',
      'admin.export.users-report',
    ],
    []
  );

  const runSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminAuditLog({
        actionType: filters.actionType || undefined,
        entityType: filters.entityType || undefined,
        from: filters.from ? new Date(filters.from).toISOString() : undefined,
        to: filters.to ? new Date(filters.to).toISOString() : undefined,
        q: filters.q || undefined,
        limit: 50,
      });
      setEntries(data.data);
      setHasMore(Boolean(data.pagination?.hasMore));
      setNextBeforeId(data.pagination?.nextBeforeId ?? null);
    } catch (err) {
      const apiMessage = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      setError(apiMessage || 'Не удалось загрузить журнал действий');
    } finally {
      setLoading(false);
    }
  };
  const loadMore = async () => {
    if (!nextBeforeId || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await getAdminAuditLog({
        actionType: filters.actionType || undefined,
        entityType: filters.entityType || undefined,
        from: filters.from ? new Date(filters.from).toISOString() : undefined,
        to: filters.to ? new Date(filters.to).toISOString() : undefined,
        q: filters.q || undefined,
        limit: 50,
        beforeId: nextBeforeId,
      });
      setEntries((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        const appended = data.data.filter((item) => !seen.has(item.id));
        return [...prev, ...appended];
      });
      setHasMore(Boolean(data.pagination?.hasMore));
      setNextBeforeId(data.pagination?.nextBeforeId ?? null);
    } catch (err) {
      const apiMessage = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      setError(apiMessage || 'Не удалось загрузить следующую страницу журнала');
    } finally {
      setLoadingMore(false);
    }
  };
  const groupedByDate = useMemo(() => {
    const map = new Map<string, AdminAuditLogEntry[]>();
    entries.forEach((entry) => {
      const key = new Date(entry.createdAt).toLocaleDateString('ru-RU');
      map.set(key, [...(map.get(key) ?? []), entry]);
    });
    return Array.from(map.entries());
  }, [entries]);
  const resolveEntityLink = (entry: AdminAuditLogEntry): string | null => {
    if (entry.entityType === 'category' && entry.entityId) return `/admin/catalog?tab=categories&categoryId=${encodeURIComponent(entry.entityId)}`;
    if (entry.entityType === 'product' && entry.entityId) return `/admin/catalog?tab=products&productId=${encodeURIComponent(entry.entityId)}`;
    if (entry.entityType === 'promocode' && entry.entityId) return `/admin/promocodes?promocodeId=${encodeURIComponent(entry.entityId)}`;
    if ((entry.entityType === 'users' || entry.entityType === 'user') && entry.entityId) return `/admin/users?userId=${encodeURIComponent(entry.entityId)}`;
    return null;
  };
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    void runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-section-header">
        <h2 className="admin-card-title">Журнал действий</h2>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => runSearch()} disabled={loading}>
          {loading ? 'Обновление...' : 'Обновить'}
        </button>
      </div>

      <form className="admin-card admin-filters-grid" onSubmit={runSearch}>
        <label className="admin-field">
          <span className="admin-field-label">Тип действия</span>
          <select
            className="admin-input"
            data-testid="admin-audit-filter-action"
            value={filters.actionType}
            onChange={(e) => setFilters((prev) => ({ ...prev, actionType: e.target.value }))}
          >
            {actionOptions.map((item) => (
              <option key={item || 'all'} value={item}>
                {item ? formatActionLabel(item) : 'Все действия'}
              </option>
            ))}
          </select>
        </label>
        <label className="admin-field">
          <span className="admin-field-label">Сущность</span>
          <input
            className="admin-input"
            data-testid="admin-audit-filter-entity"
            placeholder="category / product / promocode"
            value={filters.entityType}
            onChange={(e) => setFilters((prev) => ({ ...prev, entityType: e.target.value }))}
          />
        </label>
        <label className="admin-field">
          <span className="admin-field-label">С даты</span>
          <input className="admin-input" type="date" value={filters.from} onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))} />
        </label>
        <label className="admin-field">
          <span className="admin-field-label">По дату</span>
          <input className="admin-input" type="date" value={filters.to} onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))} />
        </label>
        <label className="admin-field admin-field-wide">
          <span className="admin-field-label">Поиск (ID, actor, причина, метка)</span>
          <input
            className="admin-input"
            data-testid="admin-audit-filter-q"
            placeholder="Например: 123456789 или rose"
            value={filters.q}
            onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
          />
        </label>
        <div>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={loading}>
            {loading ? 'Поиск...' : 'Применить фильтры'}
          </button>
        </div>
      </form>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-card">
        {entries.length === 0 ? (
          <div className="admin-field-hint">По этим фильтрам записей нет. Уточните период/поиск или сбросьте фильтры.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {groupedByDate.map(([dateLabel, dayEntries]) => (
              <div key={dateLabel} style={{ display: 'grid', gap: 8 }}>
                <div className="admin-field-label">Дата: {dateLabel}</div>
                {dayEntries.map((entry) => (
                  <div key={entry.id} className="admin-card" data-testid={`admin-audit-entry-${entry.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700 }}>{formatActionLabel(entry.actionType)}</div>
                      <div>{new Date(entry.createdAt).toLocaleTimeString('ru-RU')}</div>
                    </div>
                    <div>{formatEntityLabel(entry)}</div>
                    <div>Админ: {entry.actorTelegramId || '—'}</div>
                    <div className={`admin-ops-state ${entry.status === 'success' ? 'admin-ops-state-success' : 'admin-ops-state-error'}`}>
                      {entry.status === 'success' ? 'Успешно' : 'Ошибка'} ({entry.statusCode})
                    </div>
                    {entry.reason && <div>Причина: {entry.reason}</div>}
                    {parseChangeSummary(entry.changeSummary).length > 0 && (
                      <div>
                        <div style={{ fontWeight: 600 }}>Изменения:</div>
                        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                          {parseChangeSummary(entry.changeSummary).map((line) => (
                            <li key={`${entry.id}-${line}`}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {resolveEntityLink(entry) ? (
                      <Link className="admin-link" data-testid={`admin-audit-entity-link-${entry.id}`} to={resolveEntityLink(entry) || '#'}>
                        Перейти к сущности
                      </Link>
                    ) : (
                      <div className="admin-field-hint">Переход к сущности недоступен.</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {hasMore && (
              <button type="button" className="admin-btn admin-btn-secondary" data-testid="admin-audit-load-more" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Загрузка...' : 'Загрузить еще'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

